/*
 * Copyright (c) 2022, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
 * You may not alter or remove any copyright or other notice from copies of this content."
 */

import { AsgardeoTier } from 'data/api/interfaces';
import React, { Dispatch, SetStateAction, useContext } from 'react';
import { ResourceUsage } from 'types/generated/Billing/models/resource-usage';
import { Tier } from 'types/generated/Billing/models/tier';

interface IAsgardeoSubscriptionContext {
  organizationName?: string;
  setOrganizationName: Dispatch<SetStateAction<string | undefined>>;
  isOverviewDataFetching: boolean;
  setIsOverviewDataFetching: Dispatch<SetStateAction<boolean>>;
  monthlyUsage?: Array<ResourceUsage>;
  setMonthlyUsage: Dispatch<SetStateAction<ResourceUsage[] | undefined>>;
  currentTier: Tier;
  setCurrentTier: Dispatch<SetStateAction<Tier>>;
  refetchOverviewData: () => void;
  setRefetchOverviewData: Dispatch<SetStateAction<() => void>>;
}

const SubscriptionContext = React.createContext({
  organizationName: '',
  setOrganizationName: () => {},
  isOverviewDataFetching: true,
  setIsOverviewDataFetching: () => {},
  monthlyUsage: [],
  setMonthlyUsage: () => {},
  setCurrentTier: () => {},
  currentTier: {
    tierId: AsgardeoTier.FREE,
    name: AsgardeoTier.FREE,
  },
  refetchOverviewData: () => {},
  setRefetchOverviewData: () => {},
} as IAsgardeoSubscriptionContext);

SubscriptionContext.displayName = 'SubscriptionProvider';

const { Provider: SubscriptionProvider } = SubscriptionContext;

const useSubscription: () => IAsgardeoSubscriptionContext = () => {
  const {
    organizationName,
    setOrganizationName,
    currentTier,
    setCurrentTier,
    monthlyUsage,
    setMonthlyUsage,
    isOverviewDataFetching,
    setIsOverviewDataFetching,
    refetchOverviewData,
    setRefetchOverviewData,
  } = useContext(SubscriptionContext);

  return {
    organizationName,
    setOrganizationName,
    currentTier,
    setCurrentTier,
    monthlyUsage,
    setMonthlyUsage,
    isOverviewDataFetching,
    setIsOverviewDataFetching,
    refetchOverviewData,
    setRefetchOverviewData,
  };
};

export { SubscriptionContext, SubscriptionProvider, useSubscription };
